import React, { useEffect, useState } from 'react';
import { States } from '@/helpers/common/states';
import api, { AxiosError } from '@/services/api';
import { EventMachineSaleProductReport } from '@/model/EventMachineSaleProductReport';
import { toast } from 'react-toastify';
import { ReportControllerProps } from '../../types';
import { SaleProductMachineContent } from './ui';

interface StateProps {
  reportController: ReportControllerProps;
  setState: (state: States) => void;
  onExportPDF?: (machines: EventMachineSaleProductReport[]) => void;
}

export const SaleProductMachine: React.FC<StateProps> = ({
  reportController,
  setState,
  onExportPDF,
}): JSX.Element => {
  const { eventId, singleEvent } = reportController;
  const [machines, setMachines] = useState<EventMachineSaleProductReport[]>([]);
  const [allMachines, setAllMachines] = useState<EventMachineSaleProductReport[]>([]);
  const [posOptions, setPosOptions] = useState<{ label: string; value: string }[]>([]);

  const [serialNumber, setSerialNumber] = useState<string>('');
  const [productName, setProductName] = useState<string>('');

  const handleGetSaleProducts = async (
    filterSerial = serialNumber,
    filterProduct = productName,
  ): Promise<void> => {
    try {
      setState(States.loading);

      const params = new URLSearchParams();
      params.append('singleEvent', String(singleEvent));
      if (filterSerial.trim()) {
        params.append('serialNumber', filterSerial.trim());
      }
      if (filterProduct.trim()) {
        params.append('productName', filterProduct.trim());
      }

      const { data } = await api.get<EventMachineSaleProductReport[]>(
        `event/report/${eventId}/sale-product/machine?${params.toString()}`,
      );
      if (data) {
        setMachines(data);
        if (allMachines.length === 0 && !filterSerial.trim() && !filterProduct.trim()) {
          setAllMachines(data);
          const uniqueSerials = Array.from(
            new Set(data.map(m => m.serialNumber).filter(Boolean)),
          ).sort();
          setPosOptions(uniqueSerials.map(s => ({ label: `POS: ${s}`, value: s })));
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleClearFilter = (): void => {
    setSerialNumber('');
    setProductName('');
    handleGetSaleProducts('', '');
  };

  useEffect(() => {
    if (posOptions.length === 0) {
      const source = allMachines.length > 0 ? allMachines : machines;
      const uniqueSerials = Array.from(
        new Set(source.map(m => m.serialNumber).filter(Boolean)),
      ).sort();
      if (uniqueSerials.length > 0) {
        setPosOptions(uniqueSerials.map(s => ({ label: `POS: ${s}`, value: s })));
      }
    }
  }, [allMachines, machines]);

  useEffect(() => {
    handleGetSaleProducts();
  }, [singleEvent]);

  return (
    <SaleProductMachineContent
      machines={machines}
      allMachines={allMachines}
      serialNumber={serialNumber}
      productName={productName}
      posOptions={posOptions}
      onChangeSerialNumber={setSerialNumber}
      onChangeProductName={setProductName}
      onFilter={() => handleGetSaleProducts(serialNumber, productName)}
      onClearFilter={handleClearFilter}
      onExportPDF={
        onExportPDF
          ? (dataToExport?: EventMachineSaleProductReport[]) =>
              onExportPDF(dataToExport || machines)
          : undefined
      }
    />
  );
};
